import { Injectable, inject, EnvironmentInjector, runInInjectionContext, signal } from '@angular/core';
import { Firestore, doc, setDoc, deleteDoc, getDoc, serverTimestamp } from '@angular/fire/firestore';
import { FirebaseApp } from '@angular/fire/app';
import { environment } from '../../../environments/environment';

/** Why enabling push did not work, so the UI can say something specific. */
export type PushFailureReason =
  | 'unsupported'
  | 'not-configured'
  | 'denied'
  | 'ios-needs-install'
  | 'error';

export interface PushEnableResult {
  ok: boolean;
  reason?: PushFailureReason;
}

/**
 * Per-device web push registration, backed by Firebase Cloud Messaging.
 *
 * Tokens are stored at `users/{uid}/pushTokens/{deviceId}`, keyed by a device id
 * we persist in localStorage rather than by the token itself. FCM tokens rotate,
 * so keying by token would leave an orphan document behind on every rotation;
 * keying by device gives one stable row per browser profile that the settings
 * toggle can flip.
 *
 * Messaging is imported lazily: `getMessaging()` throws outright in browsers
 * without push support, so eager initialisation would break bootstrap on Safari
 * and on Firefox with push disabled. Lazy loading also keeps it out of the
 * initial bundle.
 *
 * Import it from `@angular/fire/messaging`, never from `firebase/messaging`.
 * There are two copies of the Firebase SDK installed — @angular/fire resolves
 * its own nested firebase 11.x, while the top-level dependency is firebase 12.x
 * — and each has its own component registry. `inject(FirebaseApp)` returns an
 * app built by AngularFire's copy, so registering messaging on the other one
 * makes `getMessaging(app)` fail with "Service messaging is not available".
 * `isSupported()` still returns true in that state, so the toggle appears to
 * work right up until you switch it on.
 */
@Injectable({ providedIn: 'root' })
export class PushNotificationService {
  private firestore = inject(Firestore);
  private firebaseApp = inject(FirebaseApp);
  private injector = inject(EnvironmentInjector);

  private static readonly DEVICE_ID_KEY = 'echo.push.deviceId';
  private static readonly ENABLED_KEY = 'echo.push.enabled';
  private static readonly SW_PATH = '/firebase-messaging-sw.js';

  /** null until the support check has run. */
  readonly supported = signal<boolean | null>(null);
  readonly enabled = signal<boolean>(false);
  readonly permission = signal<NotificationPermission>(
    typeof Notification !== 'undefined' ? Notification.permission : 'default'
  );
  /** True on iOS Safari outside an installed PWA, where push cannot work at all. */
  readonly needsInstall = signal<boolean>(false);
  readonly busy = signal<boolean>(false);

  private userId: string | null = null;
  private organizationId: string | null = null;

  /**
   * Called once the signed-in user and their organization are known. Re-registers
   * silently if this device was already opted in, and never prompts on its own —
   * the browser permission prompt is effectively one-shot per origin, so it is
   * only ever triggered from an explicit user action in `enable()`.
   */
  async init(userId: string, organizationId: string): Promise<void> {
    this.userId = userId;
    this.organizationId = organizationId;

    const supported = await this.checkSupport();
    if (!supported) {
      this.enabled.set(false);
      return;
    }

    const wasEnabled = localStorage.getItem(PushNotificationService.ENABLED_KEY) === 'true';
    if (!wasEnabled || Notification.permission !== 'granted') {
      this.enabled.set(false);
      return;
    }

    try {
      // Refresh the token on every load: the modular SDK has no onTokenRefresh,
      // so comparing what getToken() returns against what we stored is the
      // supported way to pick up a rotated token.
      await this.registerToken();
      this.enabled.set(true);
    } catch (err) {
      console.warn('Push re-registration failed:', err);
      this.enabled.set(false);
    }
  }

  /** Turn push on for this device. Must be called from a user gesture. */
  async enable(): Promise<PushEnableResult> {
    if (!this.userId || !this.organizationId) {
      return { ok: false, reason: 'error' };
    }

    this.busy.set(true);
    try {
      if (!environment.push?.vapidKey) {
        return { ok: false, reason: 'not-configured' };
      }
      if (!(await this.checkSupport())) {
        return { ok: false, reason: 'unsupported' };
      }
      if (this.isIosNeedingInstall()) {
        return { ok: false, reason: 'ios-needs-install' };
      }
      // Once denied, requestPermission() resolves 'denied' instantly and forever.
      if (Notification.permission === 'denied') {
        this.permission.set('denied');
        return { ok: false, reason: 'denied' };
      }

      const permission = await Notification.requestPermission();
      this.permission.set(permission);
      if (permission !== 'granted') {
        return { ok: false, reason: 'denied' };
      }

      await this.registerToken();
      localStorage.setItem(PushNotificationService.ENABLED_KEY, 'true');
      this.enabled.set(true);
      return { ok: true };
    } catch (err) {
      console.error('Failed to enable push notifications:', err);
      return { ok: false, reason: 'error' };
    } finally {
      this.busy.set(false);
    }
  }

  /**
   * Turn push off for this device and drop its token.
   *
   * Returns false if the stored token could not be revoked — the server reads
   * Firestore, not localStorage, so in that case it would keep pushing to a
   * device whose toggle reads "off" and the caller needs to say so.
   */
  async disable(): Promise<boolean> {
    this.busy.set(true);
    try {
      localStorage.setItem(PushNotificationService.ENABLED_KEY, 'false');
      this.enabled.set(false);
      return await this.revokeToken();
    } finally {
      this.busy.set(false);
    }
  }

  /**
   * Drop this device's token before signing out. Must run while the user is
   * still authenticated — the Firestore rules only allow a user to delete their
   * own token documents.
   */
  async cleanupOnLogout(): Promise<void> {
    try {
      await this.revokeToken();
    } catch (err) {
      console.warn('Push token cleanup on logout failed:', err);
    } finally {
      this.userId = null;
      this.organizationId = null;
      this.enabled.set(false);
    }
  }

  /** iOS only delivers web push to a PWA installed on the Home Screen. */
  isIosNeedingInstall(): boolean {
    if (typeof navigator === 'undefined' || typeof window === 'undefined') return false;

    const ua = navigator.userAgent;
    const isIos =
      /iPad|iPhone|iPod/.test(ua) ||
      // iPadOS 13+ reports itself as a Mac; the touch points give it away.
      (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
    if (!isIos) return false;

    const standalone =
      window.matchMedia?.('(display-mode: standalone)').matches ||
      (navigator as unknown as { standalone?: boolean }).standalone === true;

    return !standalone;
  }

  // --- internals -----------------------------------------------------------

  private async checkSupport(): Promise<boolean> {
    if (this.supported() !== null) return this.supported()!;

    let ok = false;
    try {
      ok =
        typeof window !== 'undefined' &&
        'Notification' in window &&
        'serviceWorker' in navigator &&
        'PushManager' in window &&
        !!environment.push?.vapidKey;

      if (ok) {
        const { isSupported } = await import('@angular/fire/messaging');
        ok = await isSupported();
      }
    } catch {
      ok = false;
    }

    this.needsInstall.set(this.isIosNeedingInstall());
    this.supported.set(ok);
    return ok;
  }

  /**
   * The service worker is a static asset outside the Angular build, so it cannot
   * read `environment.ts`. The Firebase web config is public, so we hand it over
   * on the registration query string.
   */
  private async registerServiceWorker(): Promise<ServiceWorkerRegistration> {
    const config = environment.firebaseConfig;
    const params = new URLSearchParams({
      apiKey: config.apiKey,
      authDomain: config.authDomain,
      projectId: config.projectId,
      storageBucket: config.storageBucket,
      messagingSenderId: config.messagingSenderId,
      appId: config.appId
    });

    const registration = await navigator.serviceWorker.register(
      `${PushNotificationService.SW_PATH}?${params.toString()}`,
      { scope: '/' }
    );
    await navigator.serviceWorker.ready;
    return registration;
  }

  private async registerToken(): Promise<void> {
    if (!this.userId || !this.organizationId) {
      throw new Error('Push service used before init()');
    }

    const { getMessaging, getToken } = await import('@angular/fire/messaging');
    const registration = await this.registerServiceWorker();
    const messaging = getMessaging(this.firebaseApp);

    const token = await getToken(messaging, {
      vapidKey: environment.push.vapidKey,
      serviceWorkerRegistration: registration
    });

    if (!token) {
      throw new Error('FCM returned no registration token');
    }

    const ref = this.tokenDocRef(this.userId);
    const existing = await runInInjectionContext(this.injector, () => getDoc(ref));

    await runInInjectionContext(this.injector, () =>
      setDoc(
        ref,
        {
          token,
          deviceId: this.deviceId(),
          organizationId: this.organizationId,
          userAgent: navigator.userAgent.slice(0, 512),
          platform: 'web',
          enabled: true,
          createdAt: existing.exists() ? existing.data()?.['createdAt'] ?? serverTimestamp() : serverTimestamp(),
          lastSeenAt: serverTimestamp()
        },
        { merge: true }
      )
    );
  }

  /**
   * Drop the stored token document and the browser subscription.
   * @return true if the server will definitely stop pushing to this device.
   */
  private async revokeToken(): Promise<boolean> {
    const uid = this.userId;
    let revoked = true;

    // Clear the stored document first, while we still know who we are.
    if (uid) {
      const ref = this.tokenDocRef(uid);
      try {
        await runInInjectionContext(this.injector, () => deleteDoc(ref));
      } catch (deleteErr) {
        console.warn('Failed to delete push token document:', deleteErr);
        // Fall back to flagging it disabled — the send path filters on
        // `enabled == true`, so this is enough to stop delivery.
        try {
          await runInInjectionContext(this.injector, () =>
            setDoc(ref, { enabled: false, lastSeenAt: serverTimestamp() }, { merge: true })
          );
        } catch (flagErr) {
          console.error('Failed to disable push token document:', flagErr);
          revoked = false;
        }
      }
    }

    if (this.supported() === false) return revoked;

    try {
      const { getMessaging, deleteToken } = await import('@angular/fire/messaging');
      await deleteToken(getMessaging(this.firebaseApp));
    } catch (err) {
      // Nothing registered, or the browser already dropped the subscription.
      // Harmless either way — delivery is controlled by the Firestore document.
      console.debug('deleteToken skipped:', err);
    }

    return revoked;
  }

  private tokenDocRef(userId: string) {
    return runInInjectionContext(this.injector, () =>
      doc(this.firestore, `users/${userId}/pushTokens/${this.deviceId()}`)
    );
  }

  /** Stable per-browser-profile id, so token rotation reuses one document. */
  private deviceId(): string {
    let id = localStorage.getItem(PushNotificationService.DEVICE_ID_KEY);
    if (!id) {
      id =
        typeof crypto !== 'undefined' && 'randomUUID' in crypto
          ? crypto.randomUUID()
          : `dev-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem(PushNotificationService.DEVICE_ID_KEY, id);
    }
    return id;
  }
}
